import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Ruler, ShieldCheck, Camera, Fence, Droplets, Droplet, Waves, Wind, Clock,
  ArrowRight, Check, MapPin, Loader2, Users, Navigation, ExternalLink, Phone,
} from 'lucide-react';
import * as api from '../lib/api';
import { useSearchStore } from '../store/useSearchStore';
import { VEHICLE_KINDS } from '../lib/fit';
import { HOME_LOCATION } from '../lib/brand';
import { formatCents } from '../lib/format';
import { useSeo, useJsonLd, selfStorageSchema, SITE_URL } from '../lib/seo';
import VehicleSilhouette from '../components/ui/VehicleSilhouette';
import VehicleStorageSection from '../components/public/VehicleStorageSection';
import { directionsUrl, mapPinUrl, externalLink } from '../lib/maps';
import PublicHeader from '../components/public/PublicHeader';
import PublicFooter from '../components/public/PublicFooter';

/**
 * The size grid reads left to right as a ladder, so it is ordered by length
 * rather than by however VEHICLE_KINDS happens to be declared. Sorting a copy:
 * the declaration order is what the dropdown uses, and reordering that would
 * shuffle the type picker for no reason.
 */
const BY_LENGTH = [...VEHICLE_KINDS].sort((a, b) => a.length - b.length);

const AMENITIES = [
  ['has_gate', 'Gated entry', ShieldCheck],
  ['has_cameras', 'Camera coverage', Camera],
  ['has_perimeter_fence', 'Perimeter fence', Fence],
  ['has_onsite_staff', 'Staff on site', Users],
  ['has_dump_station', 'Dump station', Droplets],
  ['has_wash_bay', 'Wash bay', Waves],
  ['has_air_compressor', 'Air compressor', Wind],
  ['has_potable_water', 'Drinking water', Droplet],
];

/**
 * The page a stranger lands on.
 *
 * Until now `/` redirected to the map, which is the wrong first thing to show
 * someone who has just searched "RV storage Merritt Island": a crawler sees an
 * empty container, and a person sees a filter panel before they have been told
 * what this place is.
 *
 * It leads with rig fit because that is the actual differentiator — knowing a
 * 42ft fifth wheel cannot use a 40ft covered space is what stops a move-in
 * failing in the driveway.
 */
export default function HomePage() {
  const navigate = useNavigate();
  const setVehicle = useSearchStore((s) => s.setVehicle);
  const setVehicleKind = useSearchStore((s) => s.setVehicleKind);
  const vehicle = useSearchStore((s) => s.vehicle);

  const [facility, setFacility] = useState(null);
  // The search result carries availability and rates but not the street line,
  // phone or hours. Those come from the facility record, fetched once the
  // search tells us which facility it is — cheaper than widening every search
  // result with fields only this one block reads.
  const [detail, setDetail] = useState(null);
  const [state, setState] = useState('loading');
  const [kind, setKind] = useState(vehicle?.kind ?? 'travel_trailer');
  const [length, setLength] = useState(String(vehicle?.length ?? 30));

  const showFitFor = (v) => {
    setVehicleKind(v.value);
    setVehicle({ length: v.length, width: v.width, height: v.height });
    navigate('/search');
  };

  useSeo({
    title: 'RV & Boat Storage in Merritt Island, FL',
    description:
      'Monthly RV, trailer and boat storage on Merritt Island. Enter your rig’s length and '
      + 'see exactly which spaces fit before you drive over. Gated, camera-covered, open daily.',
    path: '/',
  });
  useJsonLd('home', useMemo(() => selfStorageSchema(facility), [facility]));
  useJsonLd('website', useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Merritt Island RV Parking',
    url: SITE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/search?length={length_ft}`,
      'query-input': 'required name=length_ft',
    },
  }), []));

  useEffect(() => {
    let live = true;
    api.searchFacilities({ lat: HOME_LOCATION.lat, lng: HOME_LOCATION.lng, radius: 50 })
      .then((payload) => {
        if (!live) return;
        const first = payload.results?.[0] ?? null;
        setFacility(first);
        setState('ready');
        // Best-effort: the address block simply does not render without it,
        // rather than the page failing over a secondary detail.
        if (first?.slug) {
          api.getFacility(first.slug)
            .then((d) => { if (live) setDetail(d); })
            .catch(() => {});
        }
      })
      .catch(() => { if (live) setState('error'); });
    return () => { live = false; };
  }, []);

  const submit = (event) => {
    event.preventDefault();
    const ft = Number(length);
    setVehicleKind(kind);
    if (Number.isFinite(ft) && ft > 0) setVehicle({ length: ft });
    navigate('/search');
  };

  const sizes = facility?.space_types ?? [];

  /**
   * What is actually on site. Gate hours belong in this list rather than
   * tacked on after it — it is the fact people ask about most, and appending
   * it outside the loop is why the grid used to have an odd tile hanging off
   * the end.
   */
  const onSite = facility
    ? [
        ...AMENITIES
          .filter(([key]) => facility[key])
          .map(([key, label, Icon]) => ({ key, label, Icon })),
        {
          key: 'hours',
          Icon: Clock,
          label: facility.is_24h_access
            ? 'Gate open 24 hours'
            : `Gate ${String(facility.gate_opens_at).slice(0, 5)}–${String(facility.gate_closes_at).slice(0, 5)} daily`,
        },
      ]
    : [];

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader transparent />

      {/* Hero ------------------------------------------------------------ */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-gradient-to-b from-brand-50 via-white to-white">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:items-center lg:py-24">
          <div>
            <p className="inline-flex items-center gap-1.5 rounded-full border border-brand-200 bg-white px-3 py-1 text-xs font-bold text-brand-800">
              <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
              Merritt Island, Florida
            </p>
            <h1 className="mt-4 text-4xl font-extrabold leading-[1.1] tracking-tight text-slate-900 sm:text-5xl">
              Storage that actually fits your rig.
            </h1>
            <p className="mt-4 max-w-lg text-lg leading-relaxed text-slate-600">
              Tell us how long your RV, trailer or boat is. We will show you which spaces it fits,
              which are tight, and which will not work — before you drive over, not after.
            </p>

            <form onSubmit={submit} className="mt-8 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="field-label mb-1" htmlFor="hero-kind">What are you storing?</label>
                  <select id="hero-kind" className="field-input" value={kind}
                          onChange={(e) => setKind(e.target.value)}>
                    {VEHICLE_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="field-label mb-1" htmlFor="hero-length">How long is it?</label>
                  <div className="relative">
                    <input id="hero-length" type="number" min={5} max={70} step="0.5" required
                           className="field-input pr-10" value={length}
                           onChange={(e) => setLength(e.target.value)} />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">ft</span>
                  </div>
                </div>
              </div>
              <button type="submit"
                      className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-6 py-3 text-sm font-bold text-white hover:bg-brand-700">
                See what fits <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
              <p className="mt-2 text-center text-xs text-slate-500">
                No account needed to look. Prices shown up front.
              </p>
            </form>
          </div>

          <div className="lg:pl-8">
            <FitExplainer />
          </div>
        </div>
      </section>

      {/* Sizes and pricing ------------------------------------------------ */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <h2 className="text-2xl font-extrabold tracking-tight text-slate-900">
          Spaces and what they cost
        </h2>
        <p className="mt-2 max-w-2xl text-slate-600">
          Uncovered, covered and fully enclosed. Every space is measured, and the fit
          check uses those measurements rather than a category name.
        </p>

        {state === 'loading' && (
          <div className="mt-8 grid place-items-center py-12" role="status" aria-label="Loading availability">
            <Loader2 className="h-6 w-6 animate-spin text-brand-600" aria-hidden="true" />
          </div>
        )}

        {state === 'error' && (
          <p className="mt-8 rounded-xl border border-slate-200 bg-slate-50 p-6 text-slate-600">
            Availability is not loading right now.{' '}
            <Link to="/search" className="font-bold text-brand-700 hover:underline">Try the search page</Link>,
            or call us and we will check by hand.
          </p>
        )}

        {state === 'ready' && !facility && (
          <p className="mt-8 rounded-xl border border-slate-200 bg-slate-50 p-6 text-slate-600">
            Every space is taken at the moment. Create an account and we will let you know the
            day one opens.
          </p>
        )}

        {state === 'ready' && facility && (
          <>
            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              <Stat label="Longest space" value={`${facility.longest_unit_ft} ft`}
                    hint="Fits most Class A coaches" />
              <Stat label="From" value={`${formatCents(facility.min_rate_cents)}/mo`}
                    hint={`${facility.available_units} available now`} />
              <Stat label="Space types" value={String(sizes.length || 1)}
                    hint={sizes.length ? sizes.join(', ') : 'Uncovered'} />
            </div>

            <Link to="/search"
                  className="mt-6 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-6 py-3 text-sm font-bold text-white hover:bg-slate-700">
              See every space and price <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </>
        )}
      </section>

      {/* Vehicle storage --------------------------------------------------- */}
      {detail?.units && (
        <VehicleStorageSection units={detail.units} slug={detail.facility.slug} />
      )}

      {/* What fits where --------------------------------------------------- */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
          <h2 className="text-2xl font-extrabold tracking-tight text-slate-900">
            What fits where
          </h2>
          <p className="mt-2 max-w-2xl text-slate-600">
            Every rig below is drawn to the same scale, so the gap you see between them
            is the real one. Pick the closest to yours to check availability against it.
          </p>

          <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {BY_LENGTH.map((v) => (
              <li key={v.value}>
                <button type="button" onClick={() => showFitFor(v)}
                        className="group flex h-full w-full flex-col rounded-xl border border-slate-200 bg-white p-4 text-left transition hover:border-brand-400 hover:shadow-card focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
                  <div className="grid h-20 w-full place-items-end text-brand-600">
                    <VehicleSilhouette kind={v.value} length={v.length} height={v.height}
                                       className="h-full w-full" />
                  </div>
                  <p className="mt-3 text-sm font-extrabold text-slate-900">{v.label}</p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    about {v.length}ft long · {v.height}ft tall
                  </p>
                  <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-brand-700 group-hover:underline">
                    See spaces that fit <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </span>
                </button>
              </li>
            ))}
          </ul>

          <p className="mt-6 text-xs text-slate-500">
            Sizes are typical for the type and are only a starting point — the search uses
            whatever you enter, and a space is graded against your own measurements.
          </p>
        </div>
      </section>

      {/* Where we are ------------------------------------------------------ */}
      {detail?.facility && (
        <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
          <h2 className="text-2xl font-extrabold tracking-tight text-slate-900">Where we are</h2>
          <div className="mt-6 grid gap-4 lg:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-white p-5 lg:col-span-2">
              <p className="flex items-start gap-2 text-lg font-bold text-slate-900">
                <MapPin className="mt-1 h-5 w-5 shrink-0 text-brand-600" aria-hidden="true" />
                <span>
                  {detail.facility.address_line1}
                  {detail.facility.address_line2 && <>, {detail.facility.address_line2}</>}
                  <span className="block text-sm font-medium text-slate-600">
                    {detail.facility.city}, {detail.facility.state_code} {detail.facility.postal_code}
                  </span>
                </span>
              </p>

              <div className="mt-5 flex flex-wrap gap-3">
                {/*
                  Routed by coordinate rather than by the postal address: a yard's
                  gate is often not where the address pin lands, and arriving at the
                  wrong side of a fence towing forty feet is a bad afternoon. See
                  lib/maps.js.
                */}
                {directionsUrl(detail.facility.lat, detail.facility.lng, detail.facility.name) && (
                  <a href={directionsUrl(detail.facility.lat, detail.facility.lng, detail.facility.name)} {...externalLink}
                     className="inline-flex items-center gap-2 rounded-xl bg-brand-600 px-5 py-3 text-sm font-bold text-white hover:bg-brand-700">
                    <Navigation className="h-4 w-4" aria-hidden="true" />
                    Get directions
                    <ExternalLink className="h-3.5 w-3.5 opacity-80" aria-hidden="true" />
                    <span className="sr-only">(opens in a new tab)</span>
                  </a>
                )}
                {mapPinUrl(detail.facility.lat, detail.facility.lng) && (
                  <a href={mapPinUrl(detail.facility.lat, detail.facility.lng)} {...externalLink}
                     className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-5 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50">
                    <MapPin className="h-4 w-4 text-brand-600" aria-hidden="true" />
                    View on the map
                    <span className="sr-only">(opens in a new tab)</span>
                  </a>
                )}
                {detail.facility.phone && (
                  <a href={`tel:${detail.facility.phone.replace(/[^+\d]/g, '')}`}
                     className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-5 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50">
                    <Phone className="h-4 w-4 text-brand-600" aria-hidden="true" />
                    {detail.facility.phone}
                  </a>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <p className="flex items-center gap-2 text-sm font-bold text-slate-900">
                <Clock className="h-4 w-4 text-brand-600" aria-hidden="true" /> Gate hours
              </p>
              <p className="mt-1 text-sm text-slate-600">
                {detail.facility.is_24h_access
                  ? 'Open 24 hours'
                  : `${detail.facility.gate_opens_at?.slice(0, 5) ?? '—'} to ${detail.facility.gate_closes_at?.slice(0, 5) ?? '—'}, daily`}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* What is on site --------------------------------------------------- */}
      {onSite.length > 1 && (
        <section className="border-y border-slate-200 bg-slate-50">
          <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
            <h2 className="text-2xl font-extrabold tracking-tight text-slate-900">On site</h2>
            <ul className={`mt-6 grid gap-3 ${
              onSite.length <= 2 ? 'sm:grid-cols-2'
                : onSite.length === 4 ? 'sm:grid-cols-2'
                : 'sm:grid-cols-2 lg:grid-cols-3'}`}>
              {onSite.map(({ key, label, Icon }) => (
                <li key={key} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-brand-50">
                    <Icon className="h-4 w-4 text-brand-700" aria-hidden="true" />
                  </span>
                  <span className="text-sm font-semibold text-slate-800">{label}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Close ------------------------------------------------------------- */}
      <section className="mx-auto max-w-3xl px-4 py-20 text-center sm:px-6">
        <h2 className="text-3xl font-extrabold tracking-tight text-slate-900">
          Reserve in a few minutes
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-lg text-slate-600">
          Create an account, record your rig once, and every space you see afterwards is
          graded against it.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/register"
                className="rounded-xl bg-brand-600 px-6 py-3 text-sm font-bold text-white hover:bg-brand-700">
            Create your account
          </Link>
          <Link to="/search"
                className="rounded-xl border border-slate-300 px-6 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50">
            Browse spaces first
          </Link>
        </div>
      </section>

      <PublicFooter facility={facility} />
    </div>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1.5 text-3xl font-extrabold tracking-tight text-slate-900">{value}</p>
      <p className="mt-1 text-sm text-slate-500">{hint}</p>
    </div>
  );
}

/** Shows the three verdicts, which is faster than explaining them in prose. */
function FitExplainer() {
  const rows = [
    ['Fits', '45ft space, 38ft coach', 'border-emerald-200 bg-emerald-50 text-emerald-900', true],
    ['Tight', '40ft space, 38ft coach', 'border-amber-200 bg-amber-50 text-amber-900', true],
    ['Won’t fit', '36ft space, 38ft coach', 'border-rose-200 bg-rose-50 text-rose-900', false],
  ];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <p className="flex items-center gap-2 text-sm font-bold text-slate-900">
        <Ruler className="h-4 w-4 text-brand-600" aria-hidden="true" />
        Every space, graded against your rig
      </p>
      <ul className="mt-4 space-y-2.5">
        {rows.map(([verdict, example, tone, ok]) => (
          <li key={verdict} className={`flex items-center justify-between gap-3 rounded-xl border p-3.5 ${tone}`}>
            <span className="flex items-center gap-2 text-sm font-bold">
              {ok ? <Check className="h-4 w-4" aria-hidden="true" />
                  : <span aria-hidden="true" className="text-base leading-none">&times;</span>}
              {verdict}
            </span>
            <span className="text-xs opacity-80">{example}</span>
          </li>
        ))}
      </ul>
      <p className="mt-4 text-xs leading-relaxed text-slate-500">
        Near misses stay visible and flagged. An empty results page is less use than an
        honest &ldquo;this one will not work&rdquo;.
      </p>
    </div>
  );
}
